import { apiClient } from './client';

// Types
export type InvoiceStatus = 'DRAFT' | 'PENDING' | 'SENT' | 'PARTIALLY_PAID' | 'PAID' | 'OVERDUE' | 'CANCELLED' | 'REFUNDED';
export type InvoiceType = 'SCHOOL_FEES' | 'TRANSPORT' | 'LIBRARY' | 'UNIFORM' | 'BOOKS' | 'MEALS' | 'ACTIVITIES' | 'EXAM_FEES' | 'OTHER';
export type PaymentMethod = 'CASH' | 'MPESA' | 'BANK_TRANSFER' | 'CARD' | 'CHEQUE' | 'WALLET';

export interface InvoiceItem {
  _id?: string;
  description: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  discountType: 'FIXED' | 'PERCENTAGE';
  taxRate: number;
  total: number;
}

export interface InvoicePayment {
  _id?: string;
  amount: number;
  paymentMethod: PaymentMethod;
  reference: string;
  transactionId?: string;
  notes?: string;
  receivedBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  paidAt: string;
}

export interface Invoice {
  _id: string;
  institutionId: string;
  invoiceNumber: string;
  type: InvoiceType;
  status: InvoiceStatus;

  customerId?: string;
  customerType: 'STUDENT' | 'PARENT' | 'STAFF' | 'EXTERNAL';
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;

  studentId?: string;
  studentName?: string;
  gradeId?: string;
  streamId?: string;

  title: string;
  description?: string;
  items: InvoiceItem[];

  subtotal: number;
  totalDiscount: number;
  totalTax: number;
  grandTotal: number;
  paidAmount: number;
  balanceDue: number;
  currency: string;

  issueDate: string;
  dueDate: string;
  paidDate?: string;

  termId?: string;
  termName?: string;
  academicYear?: string;

  payments: InvoicePayment[];

  notes?: string;
  internalNotes?: string;
  attachments?: string[];

  remindersSent: number;
  lastReminderSentAt?: string;

  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  updatedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  cancelledBy?: string;
  cancelledAt?: string;
  cancellationReason?: string;

  createdAt: string;
  updatedAt: string;
}

export interface InvoiceTemplate {
  _id: string;
  institutionId: string;
  name: string;
  type: InvoiceType;
  title: string;
  description?: string;
  items: InvoiceItem[];
  currency: string;
  dueDays: number;
  notes?: string;
  isActive: boolean;
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceFilters {
  status?: InvoiceStatus;
  type?: InvoiceType;
  customerId?: string;
  studentId?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  overdue?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface InvoiceStats {
  summary: {
    totalInvoices: number;
    totalAmount: number;
    totalPaid: number;
    totalOutstanding: number;
  };
  byStatus: Array<{
    _id: InvoiceStatus;
    count: number;
    totalAmount: number;
    paidAmount: number;
    balanceDue: number;
  }>;
  byType: Array<{
    _id: InvoiceType;
    count: number;
    totalAmount: number;
    paidAmount: number;
  }>;
  overdueCount: number;
}

export interface CreateInvoiceData {
  type: InvoiceType;
  customerId?: string;
  customerType: 'STUDENT' | 'PARENT' | 'STAFF' | 'EXTERNAL';
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  studentId?: string;
  studentName?: string;
  parentId?: string;
  gradeId?: string;
  streamId?: string;
  title: string;
  description?: string;
  items: Omit<InvoiceItem, '_id' | 'total'>[];
  dueDate: string;
  termId?: string;
  termName?: string;
  academicYear?: string;
  notes?: string;
  internalNotes?: string;
  status?: 'DRAFT' | 'PENDING';
}

export interface RecordPaymentData {
  amount: number;
  paymentMethod: PaymentMethod;
  reference?: string;
  notes?: string;
}

export interface BulkCreateInvoiceData {
  customerId?: string;
  customerType: 'STUDENT' | 'PARENT' | 'STAFF' | 'EXTERNAL';
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  studentId?: string;
  studentName?: string;
  gradeId?: string;
  streamId?: string;
  termId?: string;
  termName?: string;
  academicYear?: string;
  // If not using template
  type?: InvoiceType;
  title?: string;
  description?: string;
  items?: Omit<InvoiceItem, '_id' | 'total'>[];
  dueDate?: string;
  notes?: string;
}

// API Functions

// Get all invoices with filters
export async function getInvoices(filters: InvoiceFilters = {}) {
  const params = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });

  const response = await apiClient.get(`/invoices?${params.toString()}`);
  return response.data;
}

// Get single invoice by ID
export async function getInvoiceById(id: string) {
  const response = await apiClient.get(`/invoices/${id}`);
  return response.data;
}

// Create new invoice
export async function createInvoice(data: CreateInvoiceData) {
  const response = await apiClient.post('/invoices', data);
  return response.data;
}

// Update invoice
export async function updateInvoice(id: string, data: Partial<CreateInvoiceData>) {
  const response = await apiClient.put(`/invoices/${id}`, data);
  return response.data;
}

// Record payment for invoice
export async function recordPayment(id: string, data: RecordPaymentData) {
  const response = await apiClient.post(`/invoices/${id}/payment`, data);
  return response.data;
}

// Send invoice
export async function sendInvoice(id: string) {
  const response = await apiClient.post(`/invoices/${id}/send`);
  return response.data;
}

// Cancel invoice
export async function cancelInvoice(id: string, reason: string) {
  const response = await apiClient.post(`/invoices/${id}/cancel`, { reason });
  return response.data;
}

// Delete invoice (draft only)
export async function deleteInvoice(id: string) {
  const response = await apiClient.delete(`/invoices/${id}`);
  return response.data;
}

// Get invoice statistics
export async function getInvoiceStats(params?: { startDate?: string; endDate?: string; type?: InvoiceType }) {
  const searchParams = new URLSearchParams();

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.append(key, value);
    });
  }

  const response = await apiClient.get(`/invoices/stats?${searchParams.toString()}`);
  return response.data;
}

// Bulk create invoices
export async function bulkCreateInvoices(invoices: BulkCreateInvoiceData[], templateId?: string) {
  const response = await apiClient.post('/invoices/bulk', { invoices, templateId });
  return response.data;
}

// Template APIs

// Get all templates
export async function getInvoiceTemplates(isActive?: boolean) {
  const params = isActive !== undefined ? `?isActive=${isActive}` : '';
  const response = await apiClient.get(`/invoices/templates${params}`);
  return response.data;
}

// Create template
export async function createInvoiceTemplate(data: {
  name: string;
  type: InvoiceType;
  title: string;
  description?: string;
  items: Omit<InvoiceItem, '_id' | 'total'>[];
  dueDays?: number;
  notes?: string;
}) {
  const response = await apiClient.post('/invoices/templates', data);
  return response.data;
}

// Update template
export async function updateInvoiceTemplate(id: string, data: Partial<{
  name: string;
  type: InvoiceType;
  title: string;
  description?: string;
  items: Omit<InvoiceItem, '_id' | 'total'>[];
  dueDays?: number;
  notes?: string;
  isActive: boolean;
}>) {
  const response = await apiClient.put(`/invoices/templates/${id}`, data);
  return response.data;
}

// Delete template
export async function deleteInvoiceTemplate(id: string) {
  const response = await apiClient.delete(`/invoices/templates/${id}`);
  return response.data;
}

// Helper functions

export function getStatusColor(status: InvoiceStatus): string {
  const colors: Record<InvoiceStatus, string> = {
    DRAFT: 'gray',
    PENDING: 'yellow',
    SENT: 'blue',
    PARTIALLY_PAID: 'orange',
    PAID: 'green',
    OVERDUE: 'red',
    CANCELLED: 'gray',
    REFUNDED: 'purple',
  };
  return colors[status] || 'gray';
}

export function getTypeLabel(type: InvoiceType): string {
  const labels: Record<InvoiceType, string> = {
    SCHOOL_FEES: 'School Fees',
    TRANSPORT: 'Transport',
    LIBRARY: 'Library',
    UNIFORM: 'Uniform',
    BOOKS: 'Books',
    MEALS: 'Meals',
    ACTIVITIES: 'Activities',
    EXAM_FEES: 'Exam Fees',
    OTHER: 'Other',
  };
  return labels[type] || type;
}

export function formatCurrency(amount: number, currency: string = 'KES'): string {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
  }).format(amount);
}
